import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Grid,
    TextField,
    InputAdornment,
} from '@mui/material';
import {
    ArrowBack,
    Search,
} from '@mui/icons-material';
import { ComparisonView } from './ComparisonView';
import { CandidateCard } from './components/CandidateCard';
import { TestingHeader } from './components/TestingHeader';
import { Candidate, ICandidateStates } from 'types/jobPipeline';
import { toast } from 'react-toastify';
import { aiMatchingService } from 'services/aiMatchingService';
import { TestResult } from './types';

interface TestCandidate {
    id: string;
    name: string;
    avatar?: string;
    tier: number;
    score: number;
    appliedDate: string;
    modifiedDate: string;
    tags: ICandidateStates[];
    tasks: number;
    isFavorite: boolean;
    company?: string;
    email: string;
}

interface PromptTestingProps {
    jobId: string;
    candidates: Candidate[];
    onBack?: () => void;
    parentVersionLabel: string;
    onApplyToAll?: (versionId: string) => void;
    selectedVersionId?: string;
    parentActiveVersionId?: string;
    expertId: string;
    setIsLoading?: (loading: boolean) => void;
    initialNewResults?: TestResult[];
    initialOldResults?: TestResult[];
}

const getTierColor = (tier: number): string => {
    switch (tier) {
        case 1:
            return '#4caf50'; // Green
        case 2:
            return '#8bc34a'; // Light green
        case 3:
            return '#ffc107'; // Amber
        case 4:
            return '#ff9800'; // Orange
        case 5:
            return '#f44336'; // Red
        default:
            return '#9e9e9e'; // Grey
    }
};

const transformCandidates = (candidates: Candidate[]): TestCandidate[] => {
    return candidates.map(c => ({
        id: c.candidateId,
        name: c.fullname,
        avatar: c.image || '',
        tier: (() => {
            const tire = c.candidateResumeDetails?.companyTire;
            if (tire && !isNaN(parseInt(tire))) return parseInt(tire);
            const uniScore = c.candidateResumeDetails?.universityScore;
            if (uniScore) return Math.ceil(6 - (uniScore / 20));
            return 0;
        })(),
        score: c.candidateEvaluationDetails?.aiEvaluationScore || 0,
        appliedDate: c.appliedAt ? new Date(c.appliedAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : 'N/A',
        modifiedDate: c.lastActivityAt ? new Date(c.lastActivityAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : 'N/A',
        tags: c.candidateStates || [],
        tasks: c.incompleteTasks || 0,
        isFavorite: !!c.favorite,
        company: c.candidateResumeDetails?.company,
        email: c.email || '',
    }));
};

export const PromptTesting: React.FC<PromptTestingProps> = ({
    jobId,
    candidates,
    onBack,
    parentVersionLabel,
    onApplyToAll,
    selectedVersionId,
    parentActiveVersionId,
    expertId,
    setIsLoading: setGlobalLoading,
    initialNewResults,
    initialOldResults,
}) => {
    const testCandidates = React.useMemo(() => transformCandidates(candidates), [candidates]);
    const [searchTerm, setSearchTerm] = useState('');
    const [visibleCount, setVisibleCount] = useState(20);
    const [compareMode, setCompareMode] = useState(false);
    const [selectedCandidates, setSelectedCandidates] = useState<Set<string>>(new Set());
    const [mockFavorites, setMockFavorites] = useState<Set<string>>(
        new Set(testCandidates.filter(c => c.isFavorite).map(c => c.id))
    );
    const [showResults, setShowResults] = useState(false);

    const filteredCandidates = React.useMemo(() => {
        return testCandidates.filter(c =>
            c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [testCandidates, searchTerm]);

    // Reset visible count when search term changes
    useEffect(() => {
        setVisibleCount(20);
    }, [searchTerm]);

    // Simplified scroll handler for infinite scroll
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 50) {
            setVisibleCount(prev => Math.min(prev + 20, filteredCandidates.length));
        }
    };

    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    const handleSelectCandidate = (candidateId: string) => {
        const newSelected = new Set(selectedCandidates);
        if (newSelected.has(candidateId)) {
            newSelected.delete(candidateId);
        } else {
            // Limit to 3 candidates for testing
            if (newSelected.size < 3) {
                newSelected.add(candidateId);
            }
        }
        setSelectedCandidates(newSelected);
    };

    const handleToggleFavorite = (candidateId: string) => {
        const newFavorites = new Set(mockFavorites);
        if (newFavorites.has(candidateId)) {
            newFavorites.delete(candidateId);
        } else {
            newFavorites.add(candidateId);
        }
        setMockFavorites(newFavorites);
    };

    const [isLoading, setIsLoading] = useState(false);
    const [evaluationResults, setEvaluationResults] = useState<{ new: TestResult[], old: TestResult[] }>({
        new: initialNewResults || [],
        old: initialOldResults || []
    });

    // Auto-skip to results if initial evaluations are provided
    useEffect(() => {
        if (initialNewResults && initialNewResults.length > 0) {
            setEvaluationResults({
                new: initialNewResults,
                old: initialOldResults || []
            });
            setSelectedCandidates(new Set(initialNewResults.map(r => r.candidateId)));

            // Set compare mode if old results exist to show comparison view instead of single view
            if (initialOldResults && initialOldResults.length > 0) {
                setCompareMode(true);
            } else {
                setCompareMode(false);
            }

            setShowResults(true);
        } else {
            // If no initial results, reset to selection view
            setEvaluationResults({ new: [], old: [] });
            setShowResults(false);
            setCompareMode(false);
        }
    }, [initialNewResults, initialOldResults, selectedVersionId]);

    const handleRunTest = async () => {
        if (!selectedVersionId) return;

        setIsLoading(true);
        if (setGlobalLoading) setGlobalLoading(true);
        try {
            // Only fetch parent evaluations if we don't already have them for ALL selected candidates
            const existingOldIds = new Set(evaluationResults.old.map(r => r.candidateId));
            const shouldFetchParent = compareMode &&
                parentActiveVersionId &&
                (evaluationResults.old.length === 0 ||
                    Array.from(selectedCandidates).some(id => !existingOldIds.has(id)));

            const promises: [Promise<any>, Promise<any> | null] = [
                aiMatchingService.evaluateCandidatesData({
                    expertId,
                    jobId: jobId,
                    promptVersionId: selectedVersionId,
                    parentActiveVersionId: compareMode ? parentActiveVersionId : undefined,
                    candidates: Array.from(selectedCandidates),
                }),
                shouldFetchParent
                    ? aiMatchingService.getCandidateEvaluationsByVersion({
                        expertId,
                        jobId: jobId,
                        versionId: parentActiveVersionId!,
                        candidates: Array.from(selectedCandidates),
                    })
                    : Promise.resolve(null)
            ];

            const [draftResponse, comparisonResponse] = await Promise.all(promises);

            let oldResults: TestResult[] = [...evaluationResults.old];
            if (comparisonResponse && comparisonResponse.apiStatus === 'SUCCESS' && comparisonResponse.output.evaluations) {
                oldResults = comparisonResponse.output.evaluations.map((e: any) => ({
                    candidateId: e.candidateId,
                    score: parseFloat(e.evaluation?.final_score || '0') / 100,
                    reasoning: e.evaluation?.recommendation || 'No reasoning provided.',
                    matchStrengths: e.evaluation?.match_strengths,
                    matchGaps: e.evaluation?.match_gaps,
                    recommendation: e.evaluation?.recommendation,
                    growthPotential: e.evaluation?.growth_potential,
                    riskFactors: e.evaluation?.risk_factors,
                    culturalFit: e.evaluation?.cultural_fit,
                    interviewFocus: e.evaluation?.interview_focus,
                }));
            }

            if (draftResponse && draftResponse.apiStatus === 'SUCCESS' && draftResponse.output.results) {
                const newResults: TestResult[] = draftResponse.output.results.map((r: any) => ({
                    candidateId: r.candidateId,
                    score: parseFloat(r.evaluationResult?.final_score || '0') / 100,
                    reasoning: r.evaluationResult?.recommendation || 'No reasoning provided.',
                    matchStrengths: r.evaluationResult?.match_strengths,
                    matchGaps: r.evaluationResult?.match_gaps,
                    recommendation: r.evaluationResult?.recommendation,
                    growthPotential: r.evaluationResult?.growth_potential,
                    riskFactors: r.evaluationResult?.risk_factors,
                    culturalFit: r.evaluationResult?.cultural_fit,
                    interviewFocus: r.evaluationResult?.interview_focus,
                }));

                setEvaluationResults({
                    new: newResults,
                    old: oldResults,
                });
                setShowResults(true);
            } else {
                toast.error('Evaluation failed');
            }
        } catch (error) {
            console.error('Error evaluating candidates:', error);
            toast.error('Failed to evaluate candidates');
        } finally {
            setIsLoading(false);
            if (setGlobalLoading) setGlobalLoading(false);
        }
    };

    // Prepare data for ComparisonView
    const comparisonCandidates = Array.from(selectedCandidates).map(candidateId => {
        const candidate = testCandidates.find(c => c.id === candidateId);
        if (!candidate) return null;
        return {
            id: candidate.id,
            name: candidate.name,
            title: candidate.company || 'Candidate', // Best guess for title
            avatar: candidate.avatar,
        };
    }).filter(Boolean) as any[];


    if (showResults) {
        return (
            <ComparisonView
                candidates={comparisonCandidates}
                newResults={evaluationResults.new}
                oldResults={evaluationResults.old}
                compareWithActive={compareMode}
                parentVersion={parentVersionLabel}
                timestamp={new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ', ' + new Date().toLocaleDateString([], { month: 'short', day: '2-digit' })}
                onBack={() => setShowResults(false)}
                onReEvaluate={handleRunTest}
                onApplyToAll={() => {
                    if (onApplyToAll && selectedVersionId) {
                        onApplyToAll(selectedVersionId);
                    }
                }}
            />
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {onBack && (
                <Box sx={{ mb: 2 }}>
                    <Button
                        variant="text"
                        startIcon={<ArrowBack />}
                        onClick={onBack}
                        sx={{
                            textTransform: 'none',
                            color: 'text.secondary',
                            '&:hover': { bgcolor: 'transparent', color: 'primary.main' }
                        }}
                    >
                        Back to Editor
                    </Button>
                </Box>
            )}

            <Box ref={scrollContainerRef} onScroll={handleScroll} sx={{ flex: 1, overflow: 'auto', pr: 0.5, mb: 2 }}>
                <TestingHeader
                    compareMode={compareMode}
                    onCompareModeChange={setCompareMode}
                />

                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search candidates by name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 20, color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: 1.5, bgcolor: 'white' }
                        }}
                    />
                </Box>

                <Grid container spacing={2}>
                    {filteredCandidates.slice(0, visibleCount).map((candidate) => (
                        <Grid item xs={6} key={candidate.id}>
                            <CandidateCard
                                candidate={candidate}
                                isSelected={selectedCandidates.has(candidate.id)}
                                onSelect={handleSelectCandidate}
                                isFavorite={mockFavorites.has(candidate.id)}
                                onToggleFavorite={handleToggleFavorite}
                                getTierColor={getTierColor}
                                isSelectionDisabled={selectedCandidates.size >= 3 && !selectedCandidates.has(candidate.id)}
                            />
                        </Grid>
                    ))}
                    {filteredCandidates.length === 0 && (
                        <Grid item xs={12}>
                            <Box sx={{ p: 4, textAlign: 'center', opacity: 0.6 }}>
                                <Typography variant="body2">No candidates found{searchTerm ? ` for "${searchTerm}"` : ''}.</Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>

                {/* Lazy loading sentinel */}
                {visibleCount < filteredCandidates.length && (
                    <Box sx={{ height: 40, display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                        <CircularProgress size={20} />
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                    {selectedCandidates.size} of 3 candidates selected
                </Typography>
                <Button
                    variant="contained"
                    onClick={handleRunTest}
                    disabled={selectedCandidates.size === 0 || isLoading}
                    startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ textTransform: 'none', fontWeight: 600, px: 3, py: 1 }}
                >
                    {isLoading ? 'Evaluating...' : 'Run Test →'}
                </Button>
            </Box>
        </Box>
    );
};
